import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIsAdmin } from '@hooks/usePermission';
import { useLocation, useHistory } from 'react-router-dom';

import { useEmployeeListRoster } from '../../../hooks/useEmployeeListRoster';
import {
  getQuickBooksAuthUrl,
  disconnectQuickBooks,
  autoMapQuickBooksEmployees,
  exportAllEmployeesToQuickBooks,
  getQuickBooksEmployees,
  createAndMapQuickBooksEmployee,
  saveQuickBooksMappings,
  syncQuickBooksTimeRange,
  importQuickBooksEmployees,
  previewQuickBooksImport,
  subscribeToQBStatus,
  subscribeToQBImportLogs,
  getQBImportLogDetails,
} from '../../../services/quickbooks';
import { ImportPreviewModal } from './ImportPreviewModal.component';
import {
  addNotification,
  notificationStates,
} from '../../../components/Notification';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { Button, buttonVariants, buttonSizes } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import styles from './QuickBooksCard.module.scss';

const QB_CONNECTED_PARAM = 'qb_connected';
const QB_ERROR_PARAM = 'qb_error';

const ERROR_MESSAGES = {
  access_denied: 'QuickBooks authorization was cancelled.',
  missing_params: 'Authorization failed: missing parameters from QuickBooks.',
  invalid_state: 'Authorization failed: invalid or missing session state.',
  state_expired: 'Authorization session expired. Please try again.',
  server_error:
    'A server error occurred during authorization. Please try again.',
};

function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function fmtDate(dateObj) {
  if (!dateObj) return '—';
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(dateObj);
}

export const QuickBooksCard = () => {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeTeamId = useSelector(
    (s) => s.team?.active?.activeTeamid ?? s.team?.active?.activeTeamId
  );
  const location = useLocation();
  const history = useHistory();

  const companyId = userProfile?.activeCompany?.companyId;
  const userId = userProfile?.id;
  const isAdmin = useIsAdmin();
  const teamId = activeTeamId;

  const { employees: prodaffEmployees, isLoading: rosterLoading } =
    useEmployeeListRoster(teamId);

  const [qbStatus, setQbStatus] = useState({
    connected: false,
    realmId: null,
    connectedAt: null,
    connectedBy: null,
    employeeMappings: {},
    employeeMappingsUpdatedAt: null,
    lastSyncAt: null,
    lastSyncStats: null,
  });

  const [qbEmployees, setQbEmployees] = useState([]);
  const [qbEmployeesLoading, setQbEmployeesLoading] = useState(false);

  // Pending local mapping changes: { [userId]: { qbEmployeeId, qbEmployeeName } | null }
  const [pendingMappings, setPendingMappings] = useState({});

  const [connecting, setConnecting] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [savingMappings, setSavingMappings] = useState(false);
  const [creatingFor, setCreatingFor] = useState(null); // userId currently being created

  const [syncRange, setSyncRange] = useState({
    start: firstOfMonthStr(),
    end: todayStr(),
  });
  const [syncing, setSyncing] = useState(false);
  const [importing, setImporting] = useState(false);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);

  const [historyLogs, setHistoryLogs] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsTitle, setDetailsTitle] = useState('');

  // ── OAuth redirect handling ─────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const connected = params.get(QB_CONNECTED_PARAM);
    const errorCode = params.get(QB_ERROR_PARAM);

    if (!connected && !errorCode) return;

    if (connected === 'true') {
      addNotification({
        message: 'QuickBooks connected successfully.',
        level: notificationStates.success,
      });
    } else if (errorCode) {
      addNotification({
        message:
          ERROR_MESSAGES[errorCode] || 'QuickBooks authorization failed.',
        level: notificationStates.error,
      });
    }

    params.delete(QB_CONNECTED_PARAM);
    params.delete(QB_ERROR_PARAM);
    const cleanSearch = params.toString();
    history.replace({
      pathname: location.pathname,
      search: cleanSearch ? `?${cleanSearch}` : '',
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Firestore real-time status ──────────────────────────────────────────────
  useEffect(() => {
    if (!companyId) return;
    const unsub = subscribeToQBStatus(companyId, setQbStatus);
    return unsub;
  }, [companyId]);

  // ── Import history subscription ─────────────────────────────────────────────
  useEffect(() => {
    if (!companyId || !qbStatus.connected) return;
    const unsub = subscribeToQBImportLogs(companyId, setHistoryLogs);
    return unsub;
  }, [companyId, qbStatus.connected]);

  // ── Load QB employees when connected ───────────────────────────────────────
  useEffect(() => {
    if (!qbStatus.connected || !companyId) {
      setQbEmployees([]);
      return;
    }
    setQbEmployeesLoading(true);
    getQuickBooksEmployees(companyId)
      .then(({ employees }) => setQbEmployees(employees || []))
      .catch(() => {
        addNotification({
          message: 'Failed to load QuickBooks employees.',
          level: notificationStates.error,
        });
      })
      .finally(() => setQbEmployeesLoading(false));
  }, [qbStatus.connected, companyId]);

  // ── Effective mappings: Firestore + pending overrides ─────────────────────
  const effectiveMappings = useMemo(() => {
    const base = qbStatus.employeeMappings || {};
    const merged = { ...base };
    for (const [uid, val] of Object.entries(pendingMappings)) {
      if (val === null) {
        delete merged[uid];
      } else {
        merged[uid] = val;
      }
    }
    return merged;
  }, [qbStatus.employeeMappings, pendingMappings]);

  const hasPendingChanges = Object.keys(pendingMappings).length > 0;

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleConnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can connect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setConnecting(true);
    try {
      const { authUrl } = await getQuickBooksAuthUrl(companyId, userId);
      window.location.href = authUrl;
    } catch {
      addNotification({
        message:
          'Failed to initiate QuickBooks authorization. Please try again.',
        level: notificationStates.error,
      });
      setConnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleDisconnect = useCallback(async () => {
    if (!isAdmin) {
      addNotification({
        message: 'Only administrators can disconnect integrations.',
        level: notificationStates.error,
      });
      return;
    }
    setDisconnecting(true);
    try {
      await disconnectQuickBooks(companyId, userId);
      setPendingMappings({});
      addNotification({
        message: 'QuickBooks disconnected.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to disconnect QuickBooks. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setDisconnecting(false);
    }
  }, [companyId, userId, isAdmin]);

  const handleAutoMap = useCallback(async () => {
    setAutoMapping(true);
    try {
      const { mapped, unmatched } = await autoMapQuickBooksEmployees(
        companyId,
        teamId
      );
      setPendingMappings({});
      const total = mapped + (unmatched?.length ?? 0);
      addNotification({
        message: `Auto-mapped ${mapped} of ${total} employees by email.`,
        level:
          mapped > 0 ? notificationStates.success : notificationStates.warning,
      });
    } catch {
      addNotification({
        message: 'Auto-mapping failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setAutoMapping(false);
    }
  }, [companyId, teamId]);

  const handleExportAll = useCallback(async () => {
    setExportingAll(true);
    try {
      const { mapped, created, skipped, errors } =
        await exportAllEmployeesToQuickBooks(companyId, teamId);
      setPendingMappings({});
      const parts = [];
      if (created > 0) parts.push(`${created} created`);
      if (mapped > 0) parts.push(`${mapped} matched by email`);
      if (skipped > 0) parts.push(`${skipped} already mapped`);
      if (errors?.length > 0) parts.push(`${errors.length} failed`);
      addNotification({
        message: `Export complete: ${parts.join(', ') || 'nothing to do'}.`,
        level:
          errors?.length > 0
            ? notificationStates.warning
            : notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to export employees. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setExportingAll(false);
    }
  }, [companyId, teamId]);

  const handleSelectMapping = useCallback(
    (prodaffUserId, qbEmployeeId) => {
      if (!qbEmployeeId) {
        setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
        return;
      }
      const emp = qbEmployees.find((e) => e.id === qbEmployeeId);
      if (!emp) return;
      setPendingMappings((prev) => ({
        ...prev,
        [prodaffUserId]: { qbEmployeeId: emp.id, qbEmployeeName: emp.name },
      }));
    },
    [qbEmployees]
  );

  const handleUnmap = useCallback((prodaffUserId) => {
    setPendingMappings((prev) => ({ ...prev, [prodaffUserId]: null }));
  }, []);

  const handleSaveMappings = useCallback(async () => {
    if (!hasPendingChanges) return;
    setSavingMappings(true);
    try {
      await saveQuickBooksMappings(companyId, pendingMappings);
      setPendingMappings({});
      addNotification({
        message: 'Employee mappings saved.',
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Failed to save mappings. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSavingMappings(false);
    }
  }, [companyId, pendingMappings, hasPendingChanges]);

  const handleCreateAndMap = useCallback(
    async (emp) => {
      setCreatingFor(emp.userId);
      try {
        await createAndMapQuickBooksEmployee(
          companyId,
          emp.userId,
          emp.name,
          emp.email
        );
        addNotification({
          message: `${emp.name} created in QuickBooks and mapped.`,
          level: notificationStates.success,
        });
      } catch {
        addNotification({
          message: `Failed to create ${emp.name} in QuickBooks. Please try again.`,
          level: notificationStates.error,
        });
      } finally {
        setCreatingFor(null);
      }
    },
    [companyId]
  );

  const handleSync = useCallback(async () => {
    if (!syncRange.start || !syncRange.end) {
      addNotification({
        message: 'Please select a valid date range.',
        level: notificationStates.error,
      });
      return;
    }
    setSyncing(true);
    try {
      const result = await syncQuickBooksTimeRange(
        companyId,
        teamId,
        syncRange.start,
        syncRange.end
      );
      addNotification({
        message: `Sync complete. ${result.synced ?? 0} records synced.`,
        level: notificationStates.success,
      });
    } catch {
      addNotification({
        message: 'Sync failed. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setSyncing(false);
    }
  }, [companyId, teamId, syncRange]);

  // Compute a live preview of what importing QB employees will do.
  // Derived from already-loaded data — no extra API call needed.
  const importPreview = useMemo(() => {
    if (!qbEmployees.length) return null;
    const mappedQbIds = new Set(
      Object.values(effectiveMappings).map((m) => m.qbEmployeeId)
    );
    const prodaffEmails = new Set(
      prodaffEmployees.map((e) => e.email?.toLowerCase()).filter(Boolean)
    );
    let toCreate = 0,
      toLink = 0,
      toSkip = 0,
      noEmail = 0;
    for (const qbEmp of qbEmployees) {
      if (mappedQbIds.has(qbEmp.id)) {
        toSkip++;
        continue;
      }
      if (!qbEmp.email) {
        noEmail++;
        continue;
      }
      if (prodaffEmails.has(qbEmp.email)) toLink++;
      else toCreate++;
    }
    return { toCreate, toLink, toSkip, noEmail };
  }, [qbEmployees, effectiveMappings, prodaffEmployees]);

  const handleOpenImportPreview = useCallback(async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    setPreviewData(null);
    setImportResults(null);
    try {
      const data = await previewQuickBooksImport(companyId, teamId, userId);
      setPreviewData(data);
    } catch {
      addNotification({
        message: 'Failed to load import preview. Please try again.',
        level: notificationStates.error,
      });
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }, [companyId, teamId, userId]);

  const handleClosePreviewModal = useCallback(() => {
    if (importing) return;
    setPreviewOpen(false);
    setPreviewData(null);
    setImportResults(null);
  }, [importing]);

  const handleConfirmImport = useCallback(async () => {
    setImporting(true);
    try {
      const result = await importQuickBooksEmployees(companyId, teamId, userId);
      setImportResults(result); // switch modal to results mode
    } catch {
      addNotification({
        message:
          'Failed to import employees from QuickBooks. Please try again.',
        level: notificationStates.error,
      });
    } finally {
      setImporting(false);
    }
  }, [companyId, teamId, userId]);

  const handleViewDetails = useCallback(
    async (log) => {
      setDetailsLoading(true);
      setDetailsOpen(true);
      setDetailsData(null);
      setDetailsTitle(`QB Import · ${fmtDate(log.importedAt)}`);
      try {
        const detail = await getQBImportLogDetails(companyId, log.id);
        setDetailsData({
          ...log.stats,
          employeeResults: detail.employees || [],
        });
      } catch {
        addNotification({
          message: 'Failed to load import details.',
          level: notificationStates.error,
        });
        setDetailsOpen(false);
      } finally {
        setDetailsLoading(false);
      }
    },
    [companyId]
  );

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <>
      <ImportPreviewModal
        isOpen={previewOpen}
        onClose={handleClosePreviewModal}
        onConfirm={handleConfirmImport}
        preview={previewData}
        loading={previewLoading}
        confirming={importing}
        source="QuickBooks"
        results={importResults}
      />
      <ImportPreviewModal
        isOpen={detailsOpen}
        onClose={() => {
          setDetailsOpen(false);
          setDetailsData(null);
        }}
        preview={null}
        loading={detailsLoading}
        confirming={false}
        source="QuickBooks"
        results={detailsData}
        resultsTitle={detailsTitle}
      />
      {/* Connection status panel */}
      <div className={styles.statusPanel}>
        <div className={styles.statusLeft}>
          <span
            className={`${styles.badge} ${qbStatus.connected ? styles.connected : styles.disconnected}`}
          >
            {qbStatus.connected ? 'Connected' : 'Not Connected'}
          </span>
          {qbStatus.connected ? (
            <span className={styles.connectedMeta}>
              Company ID: {qbStatus.realmId}
              {qbStatus.connectedAt &&
                ` · Connected ${fmtDate(qbStatus.connectedAt)}`}
            </span>
          ) : (
            <Text
              variant={textVariantsEnum.Text_13}
              fontWeight={fontWeightEnum.Normal}
              color="gray-500"
            >
              Connect your QuickBooks account to start exporting time data.
            </Text>
          )}
        </div>
        {isAdmin &&
          (qbStatus.connected ? (
            <Button
              text={disconnecting ? 'Disconnecting…' : 'Disconnect'}
              variant={buttonVariants.secondary}
              size={buttonSizes.sm}
              onClick={handleDisconnect}
              disabled={disconnecting}
            />
          ) : (
            <Button
              text={connecting ? 'Redirecting…' : 'Connect QuickBooks'}
              variant={buttonVariants.primary}
              size={buttonSizes.sm}
              onClick={handleConnect}
              disabled={connecting}
            />
          ))}
      </div>

      {/* Employee Mapping panel — only when connected */}
      {qbStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Employee Mapping</span>
              <span className={styles.sectionSubtitle}>
                Map Prodaff employees to their QuickBooks counterparts so time
                exports are attributed correctly.
              </span>
            </div>
            <div className={styles.sectionActions}>
              <Button
                text={autoMapping ? 'Mapping…' : 'Auto-map by Email'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleAutoMap}
                disabled={
                  autoMapping || exportingAll || importing || qbEmployeesLoading
                }
              />
              <Button
                text={exportingAll ? 'Exporting…' : 'Export All to QuickBooks'}
                variant={buttonVariants.secondary}
                size={buttonSizes.sm}
                onClick={handleExportAll}
                disabled={
                  exportingAll || autoMapping || importing || qbEmployeesLoading
                }
              />
              <div className={styles.importAction}>
                <Button
                  text={importing ? 'Importing…' : 'Import from QuickBooks'}
                  variant={buttonVariants.primary}
                  size={buttonSizes.sm}
                  onClick={handleOpenImportPreview}
                  disabled={
                    importing ||
                    autoMapping ||
                    exportingAll ||
                    qbEmployeesLoading
                  }
                />
                {importPreview &&
                  (importPreview.toCreate > 0 || importPreview.toLink > 0) && (
                    <span className={styles.importPreviewHint}>
                      {[
                        importPreview.toCreate > 0 &&
                          `${importPreview.toCreate} new`,
                        importPreview.toLink > 0 &&
                          `${importPreview.toLink} to link`,
                        importPreview.noEmail > 0 &&
                          `${importPreview.noEmail} no email`,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
              </div>
            </div>
          </div>

          <div className={styles.sectionBody}>
            {rosterLoading || qbEmployeesLoading ? (
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Normal}
                color="gray-500"
              >
                Loading…
              </Text>
            ) : prodaffEmployees.length === 0 ? (
              <EmptyState
                illustration
                title="No Employees Found"
                description="No employees found in this department to map to QuickBooks Time."
              />
            ) : (
              <>
                <table className={styles.mappingTable}>
                  <thead>
                    <tr>
                      <th>Prodaff Employee</th>
                      <th>QuickBooks Employee</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prodaffEmployees.map((emp) => {
                      const mapping = effectiveMappings[emp.userId];
                      const isCreating = creatingFor === emp.userId;

                      return (
                        <tr key={emp.userId}>
                          <td>
                            <div className={styles.employeeName}>
                              {emp.name}
                            </div>
                            <div className={styles.employeeEmail}>
                              {emp.email}
                            </div>
                          </td>

                          <td>
                            {mapping ? (
                              <div className={styles.mappingCell}>
                                <span className={styles.mappedTag}>
                                  {mapping.qbEmployeeName ||
                                    mapping.qbEmployeeId}
                                  <button
                                    className={styles.unmapBtn}
                                    onClick={() => handleUnmap(emp.userId)}
                                    title="Remove mapping"
                                  >
                                    ×
                                  </button>
                                </span>
                              </div>
                            ) : (
                              <select
                                className={styles.qbSelect}
                                value=""
                                onChange={(e) =>
                                  handleSelectMapping(
                                    emp.userId,
                                    e.target.value
                                  )
                                }
                                disabled={qbEmployees.length === 0}
                              >
                                <option value="">— Select QB employee —</option>
                                {qbEmployees.map((qbe) => (
                                  <option key={qbe.id} value={qbe.id}>
                                    {qbe.name}
                                    {qbe.email ? ` (${qbe.email})` : ''}
                                  </option>
                                ))}
                              </select>
                            )}
                          </td>

                          <td>
                            <div className={styles.actionCell}>
                              {!mapping && (
                                <button
                                  className={styles.createBtn}
                                  onClick={() => handleCreateAndMap(emp)}
                                  disabled={isCreating}
                                >
                                  {isCreating ? 'Creating…' : '+ Create in QB'}
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className={styles.saveMappingsRow}>
                  {hasPendingChanges && (
                    <span className={styles.pendingNote}>Unsaved changes</span>
                  )}
                  <Button
                    text={savingMappings ? 'Saving…' : 'Save Mappings'}
                    variant={buttonVariants.primary}
                    size={buttonSizes.sm}
                    onClick={handleSaveMappings}
                    disabled={savingMappings || !hasPendingChanges}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Import History panel */}
      {qbStatus.connected && historyLogs.length > 0 && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Import History</span>
              <span className={styles.sectionSubtitle}>
                Last {historyLogs.length} import run
                {historyLogs.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className={styles.sectionBody}>
            {historyLogs.map((log) => (
              <div key={log.id} className={styles.historyRow}>
                <div className={styles.historyInfo}>
                  <span className={styles.historyDate}>
                    {fmtDate(log.importedAt)}
                  </span>
                  <span className={styles.historyMeta}>
                    by{' '}
                    {log.importedBy?.name ||
                      log.importedBy?.userId ||
                      'Unknown'}
                  </span>
                </div>
                <div className={styles.historyStats}>
                  {(log.stats?.imported || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGreen}`}>
                      {log.stats.imported} created
                    </span>
                  )}
                  {(log.stats?.invited || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipBlue}`}>
                      {log.stats.invited} invited
                    </span>
                  )}
                  {(log.stats?.mapped || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipGray}`}>
                      {log.stats.mapped} linked
                    </span>
                  )}
                  {(log.stats?.errors || 0) > 0 && (
                    <span className={`${styles.chip} ${styles.chipRed}`}>
                      {log.stats.errors} failed
                    </span>
                  )}
                  {(log.stats?.imported || 0) === 0 &&
                    (log.stats?.invited || 0) === 0 &&
                    (log.stats?.mapped || 0) === 0 &&
                    (log.stats?.errors || 0) === 0 && (
                      <span className={`${styles.chip} ${styles.chipGray}`}>
                        nothing to import
                      </span>
                    )}
                </div>
                <Button
                  text="Details"
                  variant={buttonVariants.secondary}
                  size={buttonSizes.sm}
                  onClick={() => handleViewDetails(log)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sync panel — only when connected */}
      {qbStatus.connected && (
        <div className={styles.panel}>
          <div className={styles.sectionHead}>
            <div className={styles.sectionTitleBlock}>
              <span className={styles.sectionTitle}>Sync Time Data</span>
              <span className={styles.sectionSubtitle}>
                Export tracked hours to QuickBooks Online as time activities for
                a selected date range.
              </span>
            </div>
          </div>

          <div className={styles.sectionBody}>
            <div className={styles.syncRow}>
              <div className={styles.dateField}>
                <label htmlFor="qb-start-date">From</label>
                <input
                  id="qb-start-date"
                  type="date"
                  value={syncRange.start}
                  max={syncRange.end || todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, start: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <div className={styles.dateField}>
                <label htmlFor="qb-end-date">To</label>
                <input
                  id="qb-end-date"
                  type="date"
                  value={syncRange.end}
                  min={syncRange.start}
                  max={todayStr()}
                  onChange={(e) =>
                    setSyncRange((r) => ({ ...r, end: e.target.value }))
                  }
                  disabled={syncing}
                />
              </div>
              <Button
                text={syncing ? 'Syncing…' : 'Sync Now'}
                variant={buttonVariants.primary}
                size={buttonSizes.sm}
                onClick={handleSync}
                disabled={syncing || !syncRange.start || !syncRange.end}
              />
            </div>

            {qbStatus.lastSyncAt && (
              <div className={styles.syncStats}>
                <div className={styles.syncStatsTitle}>
                  Last sync · {fmtDate(qbStatus.lastSyncAt)}
                  {qbStatus.lastSyncStats?.startDate &&
                    qbStatus.lastSyncStats?.endDate && (
                      <span style={{ fontWeight: 400, color: '#64748b' }}>
                        {' '}
                        ({qbStatus.lastSyncStats.startDate} →{' '}
                        {qbStatus.lastSyncStats.endDate})
                      </span>
                    )}
                </div>
                {qbStatus.lastSyncStats && (
                  <div className={styles.syncStatsGrid}>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Synced</span>
                      <span className={`${styles.statValue} ${styles.success}`}>
                        {qbStatus.lastSyncStats.synced ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Skipped</span>
                      <span className={`${styles.statValue} ${styles.warning}`}>
                        {qbStatus.lastSyncStats.skipped ?? 0}
                      </span>
                    </div>
                    <div className={styles.syncStatItem}>
                      <span className={styles.statLabel}>Errors</span>
                      <span className={`${styles.statValue} ${styles.error}`}>
                        {qbStatus.lastSyncStats.errors ?? 0}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};
